import React, { useState, useEffect, useCallback } from 'react'
import { Input, Button, Tag, Empty, Upload, Popconfirm, message as antMessage, Tooltip, Segmented } from 'antd'
import { PlusOutlined, CopyOutlined, DeleteOutlined, UploadOutlined, SearchOutlined, AppstoreOutlined, UnorderedListOutlined, FileTextOutlined, FilePdfOutlined, FileExcelOutlined, FileWordOutlined, FileImageOutlined, FileUnknownOutlined, PictureOutlined } from '@ant-design/icons'
import api from '../../api'

const FILE_ICON_MAP = {
  pdf: { icon: <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />, color: '#fff1f0' },
  doc: { icon: <FileWordOutlined style={{ color: '#1677ff', fontSize: 24 }} />, color: '#e6f4ff' },
  docx: { icon: <FileWordOutlined style={{ color: '#1677ff', fontSize: 24 }} />, color: '#e6f4ff' },
  xls: { icon: <FileExcelOutlined style={{ color: '#52c41a', fontSize: 24 }} />, color: '#f6ffed' },
  xlsx: { icon: <FileExcelOutlined style={{ color: '#52c41a', fontSize: 24 }} />, color: '#f6ffed' },
  txt: { icon: <FileTextOutlined style={{ color: '#666', fontSize: 24 }} />, color: '#f5f5f5' },
  jpg: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  jpeg: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  png: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  gif: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  webp: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  bmp: { icon: <FileImageOutlined style={{ color: '#fa8c16', fontSize: 24 }} />, color: '#fff7e6' },
  ppt: { icon: <PictureOutlined style={{ color: '#eb2f96', fontSize: 24 }} />, color: '#fff0f6' },
  pptx: { icon: <PictureOutlined style={{ color: '#eb2f96', fontSize: 24 }} />, color: '#fff0f6' }
}

function getFileIcon(fileName) {
  const ext = (fileName || '').split('.').pop()?.toLowerCase()
  return FILE_ICON_MAP[ext] || { icon: <FileUnknownOutlined style={{ color: '#999', fontSize: 24 }} />, color: '#f5f5f5' }
}

function isImage(fileName) {
  const ext = (fileName || '').split('.').pop()?.toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)
}

function ToolPanel({ onTemplateSelect }) {
  const [templates, setTemplates] = useState([])
  const [attachments, setAttachments] = useState([])
  const [tplSearch, setTplSearch] = useState('')
  const [tplModal, setTplModal] = useState(false)
  const [tplContent, setTplContent] = useState('')
  const [viewMode, setViewMode] = useState('tile')

  const loadTemplates = useCallback(async () => {
    const res = await api.listTemplates()
    setTemplates(res.templates || [])
  }, [])

  const loadAttachments = useCallback(async () => {
    const res = await api.listAttachments()
    setAttachments(res.attachments || [])
  }, [])

  useEffect(() => { loadTemplates(); loadAttachments() }, [loadTemplates, loadAttachments])

  const handleSaveTemplate = async () => {
    if (!tplContent.trim()) return antMessage.warning('内容不能为空')
    const res = await api.saveTemplate({ content: tplContent, platform: 'douyin', tags: [] })
    if (res.success) { antMessage.success('已添加'); setTplContent(''); setTplModal(false); loadTemplates() }
    else antMessage.error(res.message)
  }

  const handleDeleteTemplate = async (id) => { await api.deleteTemplate(id); loadTemplates() }

  const handleUploadAtt = async () => {
    const res = await api.uploadAttachments()
    if (res.success) loadAttachments()
  }

  const handleDeleteAtt = async (id) => { await api.deleteAttachment(id); loadAttachments() }

  const handleDragStart = (e, att) => {
    e.dataTransfer.setData('application/interaction-attachment', JSON.stringify({
      attachment_id: att.attachment_id,
      fileName: att.fileName,
      filePath: att.filePath
    }))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const filteredTpl = templates.filter(t => !tplSearch || t.content.includes(tplSearch))

  const formatSize = (bytes) => {
    if (!bytes) return ''
    return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)}KB` : `${(bytes / 1024 / 1024).toFixed(1)}MB`
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fafafa', borderLeft: '1px solid #e8e8e8' }}>
      {/* 话术库 (上半部) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderBottom: '1px solid #e8e8e8', overflow: 'hidden' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>话术库</span>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setTplModal(!tplModal)}>新增</Button>
        </div>

        {tplModal && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
            <Input.TextArea size="small" rows={3} value={tplContent} onChange={e => setTplContent(e.target.value)}
              placeholder="输入话术模板，支持 {用户昵称} 变量" />
            <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
              <Button size="small" type="primary" onClick={handleSaveTemplate}>保存</Button>
              <Button size="small" onClick={() => { setTplModal(false); setTplContent('') }}>取消</Button>
            </div>
          </div>
        )}

        <div style={{ padding: '6px 12px' }}>
          <Input size="small" prefix={<SearchOutlined />} placeholder="搜索话术..." value={tplSearch}
            onChange={e => setTplSearch(e.target.value)} allowClear />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 12px' }}>
          {filteredTpl.length === 0 ? (
            <Empty description="暂无话术" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 20 }} />
          ) : (
            filteredTpl.map(tpl => (
              <div key={tpl.template_id}
                onClick={() => onTemplateSelect && onTemplateSelect(tpl.content)}
                style={{
                  padding: '8px 10px', marginBottom: 6, borderRadius: 6,
                  border: '1px solid #e8e8e8', cursor: 'pointer', background: '#fff',
                  fontSize: 12, lineHeight: 1.5,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#1677ff'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}>
                <div style={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                  {tpl.content.split(/(\{[^}]+\})/).map((part, i) =>
                    part.startsWith('{') ? <Tag key={i} color="orange" style={{ fontSize: 10, margin: '0 2px' }}>{part}</Tag> : <span key={i}>{part}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 2, flexShrink: 0, marginLeft: 4 }}>
                  <Button size="small" type="text" icon={<CopyOutlined />} style={{ fontSize: 11 }}
                    onClick={e => { e.stopPropagation(); navigator.clipboard?.writeText(tpl.content); antMessage.success('已复制') }} />
                  <Popconfirm title="删除?" onConfirm={() => handleDeleteTemplate(tpl.template_id)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ fontSize: 11 }}
                      onClick={e => e.stopPropagation()} />
                  </Popconfirm>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 附件管理 (下半部) */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: '1px solid #e8e8e8' }}>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>附件管理</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Segmented size="small" options={[
              { value: 'tile', icon: <AppstoreOutlined /> },
              { value: 'list', icon: <UnorderedListOutlined /> }
            ]} value={viewMode} onChange={setViewMode} />
            <Button size="small" icon={<UploadOutlined />} onClick={handleUploadAtt} />
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
          {attachments.length === 0 ? (
            <Empty description="暂无附件，支持所有文件格式" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 10 }} />
          ) : viewMode === 'tile' ? (
            /* 图标视图 */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 6 }}>
              {attachments.map(att => (
                <Tooltip key={att.attachment_id} title={att.fileName}>
                  <div
                    draggable
                    onDragStart={e => handleDragStart(e, att)}
                    style={{ position: 'relative', cursor: 'grab', textAlign: 'center' }}>
                    <div style={{
                      width: 72, height: 56, borderRadius: 6, border: '1px solid #e8e8e8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isImage(att.fileName) ? '#f5f5f5' : getFileIcon(att.fileName).color,
                      overflow: 'hidden', margin: '0 auto'
                    }}>
                      {isImage(att.fileName) ? (
                        <img src={`attachment://${att.filePath}`} alt={att.fileName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.display = 'none' }} />
                      ) : (
                        getFileIcon(att.fileName).icon
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.fileName}
                    </div>
                    <Popconfirm title="删除?" onConfirm={() => handleDeleteAtt(att.attachment_id)}>
                      <Button size="small" danger type="text" icon={<DeleteOutlined />}
                        style={{ position: 'absolute', top: -4, right: -4, fontSize: 10, padding: '0 2px', background: 'rgba(255,255,255,0.9)', borderRadius: '50%', minWidth: 18, height: 18 }} />
                    </Popconfirm>
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            /* 列表视图 */
            <div>
              {attachments.map(att => {
                const fileInfo = getFileIcon(att.fileName)
                return (
                  <div key={att.attachment_id}
                    draggable
                    onDragStart={e => handleDragStart(e, att)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                      borderBottom: '1px solid #f0f0f0', cursor: 'grab', borderRadius: 4,
                      background: '#fff'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: fileInfo.color, flexShrink: 0, overflow: 'hidden'
                    }}>
                      {isImage(att.fileName) ? (
                        <img src={`attachment://${att.filePath}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : fileInfo.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {att.fileName}
                      </div>
                      <div style={{ fontSize: 10, color: '#999' }}>{formatSize(att.fileSize)}</div>
                    </div>
                    <Popconfirm title="删除?" onConfirm={() => handleDeleteAtt(att.attachment_id)}>
                      <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ fontSize: 11 }} />
                    </Popconfirm>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ToolPanel
